import { useMemo, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { amplifyConfig, isLocalMode } from './config/amplify';
import { Dashboard } from './pages/Dashboard';
import { ApplicationAnswers } from './pages/ApplicationAnswers';
import { InterviewQA } from './pages/InterviewQA';
import { ActivityReport } from './pages/ActivityReport';
import { AdminDashboard } from './pages/AdminDashboard';
import { AuthProvider, LocalAuthProvider } from './hooks/useAuth';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AdminGuard } from './components/AdminGuard';
import { AdminLayout } from './pages/admin/AdminLayout';
import { MetricsDashboard } from './pages/admin/MetricsDashboard';
import { CanaryAnalytics } from './pages/admin/CanaryAnalytics';
import { UserManagement } from './pages/admin/UserManagement';
import { UserDetail } from './pages/admin/UserDetail';
import { AiUsage } from './pages/admin/AiUsage';
import { Marketing } from './pages/admin/Marketing';
import { ModelManagement } from './pages/admin/ModelManagement';
import { WorkQueue } from './pages/admin/WorkQueue';
import { WebsiteDev } from './pages/admin/WebsiteDev';
import { LogViewer } from './pages/admin/LogViewer';
import { DataFlows } from './pages/admin/DataFlows';
import { PdfCalibration } from './pages/admin/PdfCalibration';
import { Pricing } from './pages/admin/Pricing';
import { GlobalSettings } from './pages/admin/GlobalSettings';
import { Communications } from './pages/admin/Communications';
import { HelpSections } from './pages/admin/HelpSections';
import { UserSwitcher } from './components/UserSwitcher';
import { HelpPanel } from './components/HelpPanel';
import { HelpProvider } from './help/HelpContext';
import { pushAuthToHelper } from './services/helperBridge';

// Configure Amplify (skip in local mode — no Cognito)
if (!isLocalMode) {
  Amplify.configure(amplifyConfig);
}

// Determine initial auth tab from URL params (?tab=signup or ?tab=signin)
function getInitialAuthState(): 'signIn' | 'signUp' {
  const params = new URLSearchParams(window.location.search);
  const tab = params.get('tab');
  if (tab === 'signup') return 'signUp';
  return 'signIn';
}

// Branded header for the Authenticator page
function AuthHeader() {
  return (
    <div style={{
      textAlign: 'center',
      padding: '2rem 1rem 1rem',
      background: 'linear-gradient(180deg, #06060c 0%, #0a0a14 100%)',
    }}>
      <a href="https://skillsnap.me" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}>
        <img
          src="/assets/skillsnap_logo.png"
          alt="SkillSnap"
          style={{ height: '36px', width: 'auto' }}
        />
        <span style={{
          fontSize: '1.25rem',
          fontWeight: 700,
          color: '#ffffff',
        }}>SkillSnap</span>
      </a>
      <p style={{
        marginTop: '0.75rem',
        fontSize: '0.875rem',
        color: '#9ca3af',
      }}>AI-powered resumes tailored to every job</p>
    </div>
  );
}

// Custom validation: reject spaces in username/email fields with a friendly message
const services = {
  async validateCustomSignUp(formData: Record<string, string>) {
    const errors: Record<string, string> = {};
    const friendlyMsg = 'Usernames cannot contain spaces. Use letters, numbers, symbols, or punctuation.';
    if (formData.username && /\s/.test(formData.username)) {
      errors.username = friendlyMsg;
    }
    if (formData.email && /\s/.test(formData.email)) {
      errors.email = friendlyMsg;
    }
    return Object.keys(errors).length > 0 ? errors : undefined;
  },
};

// Custom form fields that allow paste
const formFields = {
  signUp: {
    email: {
      order: 1,
      isRequired: true,
      label: 'Email',
      placeholder: 'Enter your email',
    },
    password: {
      order: 2,
      isRequired: true,
      label: 'Password',
      placeholder: 'Enter your password',
      autocomplete: 'new-password',
    },
    confirm_password: {
      order: 3,
      isRequired: true,
      label: 'Confirm Password',
      placeholder: 'Confirm your password',
      autocomplete: 'new-password',
    },
  },
  signIn: {
    username: {
      order: 1,
      isRequired: true,
      label: 'Email',
      placeholder: 'Enter your email',
    },
    password: {
      order: 2,
      isRequired: true,
      label: 'Password',
      placeholder: 'Enter your password',
      autocomplete: 'current-password',
    },
  },
};

// Keeps the SkillSnap Extension's bearer token fresh so its "Queue this Job"
// button can call the API as this user. No-op in local mode (no Cognito) and
// when the extension isn't installed. Re-pushes on mount, when the tab regains
// focus, and every 30 min while open — the idToken lives ~1 h.
function HelperAuthSync() {
  useEffect(() => {
    if (isLocalMode) return;
    pushAuthToHelper();
    const onVisible = () => {
      if (document.visibilityState === 'visible') pushAuthToHelper();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(pushAuthToHelper, 30 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);
  return null;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <HelpProvider>
      <HelperAuthSync />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/application-qa" element={<ApplicationAnswers />} />
        {/* Legacy URL — bookmarks/help links/UAT specs predate the rename */}
        <Route path="/answers" element={<Navigate to="/application-qa" replace />} />
        <Route path="/interview-qa" element={<InterviewQA />} />
        <Route path="/activity-report" element={<ActivityReport />} />
        <Route path="/:phase" element={<Dashboard />} />
        <Route path="/admin/monitor" element={<AdminDashboard />} />
        <Route path="/admin/f47ac10b58cc" element={<AdminGuard><AdminLayout /></AdminGuard>}>
          <Route index element={<MetricsDashboard />} />
          <Route path="dashboard" element={<MetricsDashboard />} />
          <Route path="canary-analytics" element={<CanaryAnalytics />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="users/:userid" element={<UserDetail />} />
          <Route path="ai-usage" element={<AiUsage />} />
          <Route path="marketing" element={<Marketing />} />
          <Route path="models" element={<ModelManagement />} />
          <Route path="work-queue" element={<WorkQueue />} />
          <Route path="website-dev" element={<WebsiteDev />} />
          <Route path="logs" element={<LogViewer />} />
          <Route path="data-flows" element={<DataFlows />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="pdf-calibration" element={<PdfCalibration />} />
          <Route path="global-settings" element={<GlobalSettings />} />
          <Route path="communications" element={<Communications />} />
          <Route path="help-sections" element={<HelpSections />} />
        </Route>
      </Routes>
      {/* Global context-aware help assistant (hidden on /admin routes) */}
      <HelpPanel />
      </HelpProvider>
    </BrowserRouter>
  );
}

function App() {
  const initialState = useMemo(() => getInitialAuthState(), []);

  // Local mode: skip Authenticator, use LocalAuthProvider + UserSwitcher
  if (isLocalMode) {
    return (
      <ErrorBoundary>
        <LocalAuthProvider>
          <AppRoutes />
          <UserSwitcher />
        </LocalAuthProvider>
      </ErrorBoundary>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #06060c 0%, #0a0a14 50%, #12121f 100%)' }}>
      <Authenticator
        initialState={initialState}
        formFields={formFields}
        services={services}
        components={{
          Header: AuthHeader,
          SignUp: {
            FormFields() {
              const { validationErrors: _validationErrors } = useAuthenticator();
              return (
                <>
                  <Authenticator.SignUp.FormFields />
                  {/* Re-enable paste via inline script */}
                  <script dangerouslySetInnerHTML={{
                    __html: `
                      document.querySelectorAll('input').forEach(input => {
                        input.removeAttribute('onpaste');
                        input.onpaste = null;
                      });
                    `
                  }} />
                </>
              );
            },
          },
        }}
      >
        {({ signOut: _signOut, user: _user }) => (
          <ErrorBoundary>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </ErrorBoundary>
        )}
      </Authenticator>
    </div>
  );
}

export default App;
// Build: 1770000312
